import Vue from 'vue';
import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import { downloadPdfFile } from '../../../helper/downloadHelper';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import { emitter } from 'shared/helpers/mitt';

// Cache for in-flight conversation fetches to prevent duplicate API calls
const pendingConversationFetches = new Map();

// Conversas ja buscadas e recusadas pelo filtro ativo. Sem esse registro, cada
// mensagem de uma conversa fora do filtro dispararia um `ConversationApi.show`
// novo, porque ela nunca chega a entrar em `allConversations`. Zerado sempre que
// o conjunto de filtros muda.
const filteredOutConversationIds = new Set();

// Estado de modulo (mesmo padrao de `pendingConversationFetches`), entao os
// testes precisam de um ponto explicito para zera-lo entre casos.
export const resetFilteredOutConversations = () =>
  filteredOutConversationIds.clear();

// Resultado do fetch, usado por `addMessage` para distinguir "a conversa nao
// pertence a lista atual" de "a busca falhou" -- so o primeiro vira cache.
export const CONVERSATION_FETCH_RESULT = {
  AVAILABLE: 'available',
  FILTERED_OUT: 'filtered_out',
  FAILED: 'failed',
};

import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
  isOnFoldersView,
} from './helpers/actionHelpers';
import { applyPageFilters } from './helpers';
import { hasAccessToConversation } from './helpers/permissionHelpers';
import { FILTER_MATCH, matchesAppliedFilters } from './helpers/filterMatcher';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';

// `attribute_display_type` de cada atributo customizado de conversa, indexado
// por chave. O matcher precisa disso para espelhar o cast que o backend aplica
// aos dois lados da comparacao (um `equal_to '3.0'` casa com o valor 3 num
// atributo numerico). Sem o store carregado o mapa vem vazio e o matcher trata
// esses filtros como indecidiveis.
export const conversationCustomAttributeTypes = rootGetters => {
  const getAttributes = rootGetters['attributes/getAttributesByModel'];
  if (typeof getAttributes !== 'function') return {};

  return (getAttributes('conversation_attribute') || []).reduce(
    (byKey, attribute) => ({
      ...byKey,
      [attribute.attribute_key]: attribute.attribute_display_type,
    }),
    {}
  );
};

// O conjunto de filtros que define a lista visivel: os filtros avancados
// aplicados ou, numa pasta, a query da propria pasta. Vazio quando a lista e
// montada pelos getters de aba.
export const activeConversationFilters = ({
  state,
  rootState,
  rootGetters,
}) => {
  if (state.appliedFilters.length) return state.appliedFilters;

  if (isOnFoldersView(rootState)) {
    const activeFolder = rootGetters['customViews/getActiveConversationFolder'];
    return activeFolder?.query?.payload || [];
  }

  return [];
};

// Quando um filtro avancado esta aplicado (ou uma pasta esta ativa) a ChatList
// deixa de usar os getters de aba e renderiza `allConversations` inteiro. Logo,
// qualquer conversa que entre no store por evento de websocket aparece na
// listagem. Este gate centraliza a decisao de "essa conversa pertence a lista
// que o usuario esta vendo agora?" (issue #1330).
export const shouldListConversation = (
  conversation,
  { state, rootState, rootGetters }
) => {
  const currentUser = rootGetters.getCurrentUser;
  if (!currentUser || !hasAccessToConversation(conversation, currentUser)) {
    return false;
  }

  const filters = activeConversationFilters({ state, rootState, rootGetters });

  if (filters.length) {
    // Conservador: so entra o que casa comprovadamente com o filtro. UNKNOWN
    // (atributo que o payload de websocket nao carrega) fica de fora e aparece
    // no proximo fetch da lista.
    return (
      matchesAppliedFilters(
        conversation,
        filters,
        conversationCustomAttributeTypes(rootGetters)
      ) === FILTER_MATCH.MATCHED
    );
  }

  // Pasta ativa cuja query ainda nao carregou: nao ha como decidir.
  if (isOnFoldersView(rootState)) return false;

  if (isOnMentionsView(rootState) || isOnUnattendedView(rootState)) {
    return false;
  }

  const { currentInbox } = state;

  return !currentInbox || Number(currentInbox) === conversation.inbox_id;
};

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

// actions
const actions = {
  // `params` aceita o id da conversa ou `{ id, respectFilters }`.
  // `respectFilters` e usado pelo fetch implicito do `addMessage`: o backend
  // devolve qualquer conversa que o usuario possa acessar, mas com um filtro
  // aplicado a ChatList renderiza `allConversations` inteiro -- entao adicionar
  // sem checar faz conversas fora do filtro aparecerem na lista (issue #1330).
  // Quem abre a conversa por URL (ConversationView/InboxView) segue passando so
  // o id e mantem o comportamento de sempre adicionar.
  getConversation: async (
    { commit, state, rootState, rootGetters },
    params
  ) => {
    const { id: conversationId, respectFilters = false } =
      typeof params === 'object' && params !== null ? params : { id: params };
    try {
      commit('SET_CONVERSATION_FETCHING_STATUS', true);
      const response = await ConversationApi.show(conversationId);
      const conversation = response.data;

      // Check if conversation already exists in the store
      const exists = state.allConversations.some(c => c.id === conversation.id);

      let result = CONVERSATION_FETCH_RESULT.AVAILABLE;

      if (exists) {
        // Update existing conversation
        commit(types.UPDATE_CONVERSATION, conversation);
      } else if (
        !respectFilters ||
        shouldListConversation(conversation, { state, rootState, rootGetters })
      ) {
        // Add conversation to store
        // No permission check needed here - if backend returned the conversation,
        // the user has access (backend already validated via ConversationPolicy)
        // Getters will filter conversations appropriately for each tab
        commit(types.ADD_CONVERSATION, conversation);
      } else {
        result = CONVERSATION_FETCH_RESULT.FILTERED_OUT;
      }

      // Safely update contact if sender exists (null-safe to prevent crashes)
      if (conversation.meta?.sender) {
        commit(`contacts/${types.SET_CONTACT_ITEM}`, conversation.meta.sender);
      }

      return result;
    } catch (error) {
      // Ignore error
      return CONVERSATION_FETCH_RESULT.FAILED;
    } finally {
      commit('SET_CONVERSATION_FETCHING_STATUS', false);
    }
  },

  fetchAllConversations: async ({ commit, state, dispatch }) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const params = state.conversationFilters;
      const {
        data: { data },
      } = await ConversationApi.get(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType
      );
    } catch (error) {
      // Handle error
    }
  },

  async setTakeOverConversationsIds({ commit, state, getters }) {
    // Don't make the request if we're already fetching
    if (state.uiFlags?.isFetchingTakeOverIds) {
      return;
    }
    
    // Set loading flag
    commit(types.SET_CONVERSATION_UI_FLAG, {
      isFetchingTakeOverIds: true,
    });
    
    try {
      const { data: { conversation_ids: botAgentConversationIds }} = await ConversationApi.getBotAgentConversationsIds();
      commit(types.SET_TAKE_OVER_CONVERSATIONS_IDS, botAgentConversationIds);
    } catch (error) {
      // Handle error silently
    } finally {
      commit(types.SET_CONVERSATION_UI_FLAG, {
        isFetchingTakeOverIds: false,
      });
    }
  },  

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } catch (error) {
      // Handle error
    }
  },

  emptyAllConversations({ commit }) {
    filteredOutConversationIds.clear();
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED);
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: data.payload,
      });
    } catch (error) {
      // Handle error
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch, state }, { data, after }) {
    // Ensure dataFetched is explicitly set before setting as current chat
    // This prevents showing empty state during message loading
    if (data.dataFetched === undefined) {
      Vue.set(data, 'dataFetched', false);
    }

    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED);

    if (data.dataFetched === false) {
      try {
        // Guard against empty messages array to prevent accessing undefined.id
        const beforeId = data.messages?.length ? data.messages[0].id : null;
        const params = {
          conversationId: data.id,
          ...(after != null ? { after } : {}),
          ...(beforeId != null ? { before: beforeId } : {}),
        };
        await dispatch('fetchPreviousMessages', params);

        // CRITICAL FIX: Find conversation in store instead of using stale reference
        // The 'data' parameter may have been replaced by UPDATE_CONVERSATION during async fetch
        // (e.g., when WebSocket events arrive while fetching messages)
        const conversationInStore = state.allConversations.find(c => c.id === data.id);
        if (conversationInStore) {
          Vue.set(conversationInStore, 'dataFetched', true);
        }
      } catch (error) {
        // Ignore error - but still mark as fetched to prevent infinite spinner
        const conversationInStore = state.allConversations.find(c => c.id === data.id);
        if (conversationInStore) {
          Vue.set(conversationInStore, 'dataFetched', true);
        }
      }
    }
  },

  // O erro precisa chegar a quem chamou: a barreira de permissionamento (#1339) responde 422
  // e e o chamador que desfaz a troca otimista do responsavel. Engolir a rejeicao aqui fazia
  // o .then rodar como sucesso depois de uma recusa do servidor.
  assignAgent: async ({ dispatch }, { conversationId, agentId }) => {
    const response = await ConversationApi.assignAgent({
      conversationId,
      agentId,
    });
    dispatch('setCurrentChatAssignee', response.data);
    dispatch('handleAssigneeChange', { conversation: response.data });
  },
  handleAssigneeChange: ({ commit, dispatch, rootState }, { conversation }) => {
    const currentUserId = rootState.auth.currentUser.id;
    if (conversation.assignee_id !== currentUserId) {
      commit(types.REMOVE_CONVERSATION, conversation.id);
      router.push({ name: 'inbox_dashboard' });
    } else {
      dispatch('updateConversation', conversation);
    }
  },
  checkAccess: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.checkAccess(conversationId);
      return response.data.access;
    } catch (error) {
      console.error('Error checking access:', error);
      return false;
    }
  },
  setCurrentChatAssignee({ commit }, assignee) {
    commit(types.ASSIGN_AGENT, assignee);
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {      
      const errorMessage = error?.response?.data?.message || 'OUT_OF_OFFICE';
      throw new Error(errorMessage);
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null }
  ) => {
    try {
      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },  

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addMessage({ commit, state, dispatch }, message) {
    const { conversation_id: conversationId } = message;
    const conversationExists = state.allConversations.some(
      c => c.id === conversationId
    );

    // Helper to commit message and related data
    const commitMessageData = () => {
      commit(types.ADD_MESSAGE, message);
      if (message.message_type === MESSAGE_TYPE.INCOMING) {
        commit(types.SET_CONVERSATION_CAN_REPLY, {
          conversationId,
          canReply: true,
        });
        commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
      }
    };

    // This handles race conditions where MESSAGE_CREATED arrives before CONVERSATION_CREATED
    if (!conversationExists) {
      // Ja sabemos que esta conversa nao pertence a lista atual: buscar de novo
      // a cada mensagem so geraria trafego (uma chamada por mensagem recebida).
      if (filteredOutConversationIds.has(conversationId)) {
        commitMessageData();
        return Promise.resolve();
      }

      // Check if we're already fetching this conversation
      const existingFetch = pendingConversationFetches.get(conversationId);
      if (existingFetch) {
        return existingFetch.then(commitMessageData);
      }

      // Create new fetch - add message even if fetch fails (better to have message without full conversation than lose it)
      const fetchPromise = dispatch('getConversation', {
        id: conversationId,
        respectFilters: true,
      })
        .then(result => {
          // So o resultado explicito vira cache: uma falha de rede nao pode
          // suprimir o refetch das proximas mensagens.
          if (result === CONVERSATION_FETCH_RESULT.FILTERED_OUT) {
            filteredOutConversationIds.add(conversationId);
          }
        })
        .catch(() => {
          // Proceed with message insert even if conversation fetch fails
        })
        .then(commitMessageData)
        .finally(() => {
          pendingConversationFetches.delete(conversationId);
        });

      pendingConversationFetches.set(conversationId, fetchPromise);
      return fetchPromise;
    }

    commitMessageData();
    return Promise.resolve();
  },

  updateMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation(
    { commit, state, dispatch, rootState, rootGetters },
    conversation
  ) {
    const sender = conversation.meta?.sender;
    // Mesmo conjunto de filtros usado pelo gate e por `updateConversation`:
    // numa pasta o contador tambem precisa ser atualizado.
    const hasAppliedFilters = !!activeConversationFilters({
      state,
      rootState,
      rootGetters,
    }).length;

    if (
      !shouldListConversation(conversation, { state, rootState, rootGetters })
    ) {
      return;
    }

    // CRITICAL: Check if conversation already exists to prevent ghost conversation duplicates
    // WebSocket events can arrive for conversations that are already in the store
    // (e.g., when a new message arrives on an existing conversation)
    const exists = state.allConversations.some(c => c.id === conversation.id);

    if (exists) {
      // Conversation already exists - update it instead of creating duplicate
      commit(types.UPDATE_CONVERSATION, conversation);
    } else {
      // New conversation - safe to add
      commit(types.ADD_CONVERSATION, conversation);
    }

    // Only dispatch setContact if sender exists (null-safe)
    if (sender) {
      dispatch('contacts/setContact', sender);
    }

    // Update conversation stats when a new conversation is added to the filtered list
    // This ensures the filtered_count is accurate when new conversations match the filters
    if (hasAppliedFilters) {
      emitter.emit('fetch_conversation_stats');
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation(
    { commit, dispatch, state, rootState, rootGetters },
    conversation
  ) {
    const { meta: { sender } = {} } = conversation;

    // A lista so e montada a partir de `allConversations` quando ha um conjunto
    // de filtros ativo -- filtros avancados ou a query da pasta aberta. Fora
    // disso os getters de aba cuidam da filtragem e nada e removido aqui.
    const filters = activeConversationFilters({
      state,
      rootState,
      rootGetters,
    });
    const hasAppliedFilters = filters.length > 0;
    const exists = state.allConversations.some(c => c.id === conversation.id);
    let conversationRemoved = false;

    // UPDATE_CONVERSATION so atualiza o que ja esta no store. Com filtro
    // aplicado a conversa que passa a casar (ex.: alguem assumiu o atendimento
    // sob o filtro "responsavel esta presente") precisa entrar na lista, senao
    // so aparece no proximo fetch.
    if (
      !exists &&
      hasAppliedFilters &&
      shouldListConversation(conversation, { state, rootState, rootGetters })
    ) {
      commit(types.ADD_CONVERSATION, conversation);
    } else {
      commit(types.UPDATE_CONVERSATION, conversation);
    }

    if (hasAppliedFilters && exists) {
      // Conservador na direcao oposta ao add: so remove o que comprovadamente
      // deixou de casar com o filtro. UNKNOWN mantem a conversa na lista.
      const match = matchesAppliedFilters(
        conversation,
        filters,
        conversationCustomAttributeTypes(rootGetters)
      );
      if (match === FILTER_MATCH.NOT_MATCHED) {
        commit(types.REMOVE_CONVERSATION, conversation.id);
        conversationRemoved = true;
      }
    }

    // Update conversation stats when a conversation is removed from filtered list
    if (conversationRemoved) {
      emitter.emit('fetch_conversation_stats');
    } else if (hasAppliedFilters) {
      // Even if conversation is not removed, if we have applied filters,
      // the count might have changed (e.g., conversation status changed but still matches filters)
      // Update stats to ensure accuracy
      emitter.emit('fetch_conversation_stats');
    }

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });
    if (sender) {
      dispatch('contacts/setContact', sender);
    }
    dispatch('setTakeOverConversationsIds');
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    try {
      await ConversationApi.sendEmailTranscript({ conversationId, email });
    } catch (error) {
      throw new Error(error);
    }
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, custom_attributes);
    } catch (error) {
      // Handle error
    }
  },

  toggleIAStatus: async ({ commit, dispatch }, { conversationId }) => {
    try {
      const response = await ConversationApi.toggleIA(conversationId);
      const { custom_attributes, ia_active } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, custom_attributes);
      // When activating IA, agent is unassigned — clear assignee in store
      if (ia_active) {
        dispatch('setCurrentChatAssignee', null);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  setConversationFilters({ commit }, data) {
    filteredOutConversationIds.clear();
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    filteredOutConversationIds.clear();
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  downloadPdfTranscript: async ({ commit }, conversationId) => {
    commit(types.SET_CONVERSATION_UI_FLAG, {
      isDownloadingPdf: true,
    });
    
    try {
      const response = await ConversationApi.getPdfTranscript(conversationId);
      downloadPdfFile(
        `conversation_${conversationId}_transcript.pdf`,
        response.data
      );
      commit(types.SET_CONVERSATION_UI_FLAG, {
        isDownloadingPdf: false,
      });
      return response;
    } catch (error) {
      commit(types.SET_CONVERSATION_UI_FLAG, {
        isDownloadingPdf: false,
      });
      throw error;
    }
  },
    
  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;